/**
 * JSON Schema 2020-12 → form-tree compiler — phase 27 Slice B.
 *
 * Pure logic: takes a canonical action's `inputSchema` and produces a
 * discriminated-union `FormTreeNode` the React renderer walks to draw
 * a typed form. NO DOM, NO React, NO IO — testable end-to-end via
 * vitest.
 *
 * Layer's canonical-action schema surface is small enough that a
 * vanilla compiler beats pulling in `@rjsf/core` (~100 KB) or `@formily`
 * (~500 KB). The compiler covers the features Layer uses:
 *
 *   - `type: 'string'` with `pattern`, `minLength`, `maxLength`, `format`,
 *     `enum`, `description`.
 *   - `type: 'integer'` with `minimum`, `maximum`, `description`.
 *   - `type: 'boolean'`.
 *   - `type: 'array'` with `items` (single-item shape only — Layer
 *     doesn't use tuple arrays).
 *   - `type: 'object'` with `properties` + `required` + optional `oneOf`
 *     (Layer's `oneOf` shape is exactly-one-of-N variant discriminated
 *     by which `required` field is set — phase 25's `charge_member`).
 *   - `additionalProperties: false` is the locked Layer convention; the
 *     compiler doesn't add a free-form key/value editor.
 *
 * OUT OF SCOPE at phase 27 (deferred to a future widening when an
 * action actually uses these): `$ref`, `allOf` (other than the
 * `oneOf + not(allOf)` mutual-exclusion idiom from `send_email`),
 * `dependentRequired`, `if/then/else`, tuple `prefixItems`.
 *
 * The compiler intentionally tolerates partial / weird schemas by
 * falling through to a `'text'` node with no constraints — degraded
 * but never throwing. Schemas validated upstream at Ajv compile time;
 * by the time we read them, the shape is well-formed JSON Schema, just
 * possibly using features the compiler doesn't model.
 */

// ---------------------------------------------------------------------------
// Public types — the discriminated union the renderer walks.
// ---------------------------------------------------------------------------

export type FormTextFormat = "email" | "phone" | "uuid" | "datetime";

/**
 * Hint that a field's value should come from the `/api/lookups/<entity>`
 * dropdown instead of a free-text input. Detected from the field name
 * (`member_id` UUID → 'members', `package_slug` slug → 'packages', etc.)
 * — the renderer reads this to choose `<LookupSelect>` over `<input>`.
 *
 * Keep in sync with `LOOKUP_ENTITIES` in
 * `apps/dashboard/src/repositories/lookup-options.ts`.
 */
export type FormLookupEntity =
  | "members"
  | "trainers"
  | "slots"
  | "bookings"
  | "member_memberships"
  | "member_packages"
  | "payments"
  | "packages"
  | "memberships"
  | "tags";

export interface FormTextNode {
  readonly kind: "text";
  readonly name: string;
  readonly required: boolean;
  readonly description?: string;
  readonly pattern?: string;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly format?: FormTextFormat;
  readonly lookupEntity?: FormLookupEntity;
}

export interface FormIntegerNode {
  readonly kind: "integer";
  readonly name: string;
  readonly required: boolean;
  readonly description?: string;
  readonly minimum?: number;
  readonly maximum?: number;
}

export interface FormBooleanNode {
  readonly kind: "boolean";
  readonly name: string;
  readonly required: boolean;
  readonly description?: string;
}

export interface FormEnumNode {
  readonly kind: "enum";
  readonly name: string;
  readonly required: boolean;
  readonly description?: string;
  readonly values: ReadonlyArray<string>;
}

export interface FormArrayNode {
  readonly kind: "array";
  readonly name: string;
  readonly required: boolean;
  readonly description?: string;
  readonly itemNode: FormTreeNode;
}

export interface FormObjectNode {
  readonly kind: "object";
  /** Object node may be the root (no name) or a nested property. */
  readonly name?: string;
  readonly required: ReadonlyArray<string>;
  readonly properties: Record<string, FormTreeNode>;
  readonly description?: string;
}

/**
 * `oneOf` variant — Layer's convention is "exactly one of N required-field
 * sets is satisfied". Each variant carries the `discriminator` field
 * name (the first `required` field unique to that variant) and a
 * nested form node containing just that variant's properties.
 */
export interface FormOneOfVariant {
  /**
   * Stable label the renderer uses for the radio button + the active
   * branch selector. Defaults to the first `required` field of the
   * variant if no `title` is present.
   */
  readonly discriminator: string;
  readonly node: FormObjectNode;
}

export interface FormOneOfNode {
  readonly kind: "oneOf";
  readonly name?: string;
  readonly required: boolean;
  readonly description?: string;
  readonly variants: ReadonlyArray<FormOneOfVariant>;
}

export type FormTreeNode =
  | FormTextNode
  | FormIntegerNode
  | FormBooleanNode
  | FormEnumNode
  | FormArrayNode
  | FormObjectNode
  | FormOneOfNode;

// ---------------------------------------------------------------------------
// JSON Schema narrowing helpers.
// ---------------------------------------------------------------------------

export type JsonSchema = Record<string, unknown>;

function isObj(v: unknown): v is JsonSchema {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

function getType(schema: JsonSchema): string | undefined {
  const t = schema["type"];
  return typeof t === "string" ? t : undefined;
}

function getString(schema: JsonSchema, key: string): string | undefined {
  const v = schema[key];
  return typeof v === "string" ? v : undefined;
}

function getNumber(schema: JsonSchema, key: string): number | undefined {
  const v = schema[key];
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function getEnum(schema: JsonSchema): ReadonlyArray<string> | undefined {
  const e = schema["enum"];
  if (!Array.isArray(e)) return undefined;
  const out: string[] = [];
  for (const v of e) {
    if (typeof v === "string") out.push(v);
  }
  return out.length > 0 ? out : undefined;
}

/**
 * Pattern-based format detection. Layer's actions encode formats via
 * `pattern` (UUID v7 + E.164 + ISO email) rather than JSON Schema's
 * `format` keyword. The renderer wants this so it can pick
 * `<input type="email">` vs plain text.
 */
function detectFormat(
  pattern: string | undefined,
  formatKw: string | undefined,
): FormTextFormat | undefined {
  if (formatKw === "email") return "email";
  if (formatKw === "date-time") return "datetime";
  if (pattern === undefined) return undefined;
  // (lookupEntity detection lives in `detectLookupEntity` — see below;
  // formats vs lookups are independent: a `member_id` field can be both
  // `format: 'uuid'` and `lookupEntity: 'members'`.)
  // UUID v7 — phase 00's locked pattern (RFC 9562 §5.7).
  if (pattern.includes("7[0-9a-f]{3}") && pattern.includes("[89ab]")) {
    return "uuid";
  }
  // ISO-8601 datetime — Layer's locked pattern shape used by every
  // `*_at` field (`staff_create_slot.starts_at`, etc.).
  if (
    pattern.includes("[0-9]{4}-[0-9]{2}-[0-9]{2}") &&
    pattern.includes("T[0-9]{2}:[0-9]{2}")
  ) {
    return "datetime";
  }
  // Email — phase 05's locked simple regex `[^@]+@[^@]+\.[^@]+`.
  if (/@/.test(pattern) && /\\./.test(pattern)) {
    return "email";
  }
  // E.164 — Layer's locked `^\+[1-9]\d{1,14}$`.
  if (/^\^\\\+|\+[1-9]/.test(pattern)) {
    return "phone";
  }
  return undefined;
}

/**
 * Detect the entity a `*_id` or `*_slug` text field should lookup.
 * Field-name-driven (UUID / slug patterns aren't entity-specific). Returns
 * `undefined` for fields whose name doesn't match any known mapping —
 * those keep rendering as plain text inputs.
 *
 * Mappings cover every entity reference used as input on canonical
 * actions registered as of phase 29:
 *
 *   - `member_id`               → members
 *   - `trainer_id`              → trainers
 *   - `slot_id`                 → slots
 *   - `booking_id`              → bookings
 *   - `payment_id` / `parent_payment_id` → payments
 *   - `member_membership_id`    → member_memberships
 *   - `member_package_id`       → member_packages
 *   - `package_slug`            → packages   (YAML catalog)
 *   - `membership_slug`         → memberships (YAML catalog)
 */
function detectLookupEntity(
  fieldName: string | undefined,
  pattern: string | undefined,
): FormLookupEntity | undefined {
  if (fieldName === undefined) return undefined;
  // UUID-pattern guard for the `*_id` cases — fields named `*_id` that
  // aren't UUIDs (rare but possible) should NOT become dropdowns.
  const isUuidPattern =
    pattern !== undefined &&
    pattern.includes("7[0-9a-f]{3}") &&
    pattern.includes("[89ab]");
  if (isUuidPattern) {
    if (fieldName === "member_id") return "members";
    if (fieldName === "trainer_id") return "trainers";
    if (fieldName === "slot_id") return "slots";
    if (fieldName === "booking_id") return "bookings";
    if (fieldName === "payment_id" || fieldName === "parent_payment_id") {
      return "payments";
    }
    if (fieldName === "member_membership_id") return "member_memberships";
    if (fieldName === "member_package_id") return "member_packages";
  }
  // Slug fields — YAML-backed catalog entries.
  if (fieldName === "package_slug") return "packages";
  if (fieldName === "membership_slug") return "memberships";
  return undefined;
}

// ---------------------------------------------------------------------------
// Public compiler entrypoint.
// ---------------------------------------------------------------------------

/**
 * Compile a JSON Schema 2020-12 root object into a `FormTreeNode`. The
 * root is always an object — Layer's input schemas are exclusively
 * `{type: 'object', ...}`. A non-object root falls through to an empty
 * object node (degraded).
 */
export function compileSchemaToFormTree(schema: JsonSchema): FormTreeNode {
  return compileNode(schema, undefined, true);
}

/**
 * Internal — compile a sub-schema into a node. `name` is the parent
 * object's property key (undefined at the root). `required` indicates
 * whether the parent object requires this property.
 */
function compileNode(
  schema: JsonSchema,
  name: string | undefined,
  required: boolean,
): FormTreeNode {
  // oneOf at this level — render as a discriminated variant set.
  // Layer's convention: each variant has its own `required` array
  // declaring which fields belong to it.
  //
  // EXCEPTION: when the schema ALSO declares `type: "object"`, the
  // `oneOf` is a server-side validation refinement (e.g. `lookup_member`
  // uses oneOf to constrain `identifier_value`'s pattern based on
  // `identifier_kind`). The form should render the regular object body
  // — the oneOf is invisible at the form layer. Without this guard the
  // compiler short-circuits on oneOf with empty variants (each variant
  // typically declares only `properties` overrides, no `required` of
  // its own), producing 3 empty "variant" tabs with no fields inside.
  if (
    Array.isArray(schema["oneOf"]) &&
    schema["oneOf"].length > 0 &&
    getType(schema) !== "object"
  ) {
    const description = getString(schema, "description");
    const variants: FormOneOfVariant[] = [];
    for (const raw of schema["oneOf"] as unknown[]) {
      if (!isObj(raw)) continue;
      // Each oneOf variant typically declares `required: ['field']` to
      // identify which branch applies. The PARENT schema's `properties`
      // map is the source of truth for property shapes — variants
      // typically don't re-declare properties.
      const variantRequired = Array.isArray(raw["required"])
        ? (raw["required"] as unknown[]).filter(
            (s): s is string => typeof s === "string",
          )
        : [];
      const discriminator =
        getString(raw, "title") ?? variantRequired[0] ?? "variant";
      // Build the variant's object node: properties are pulled from
      // the parent schema; required is the variant's required array.
      const parentProps = isObj(schema["properties"])
        ? (schema["properties"] as Record<string, JsonSchema>)
        : {};
      const properties: Record<string, FormTreeNode> = {};
      for (const fieldName of variantRequired) {
        const propSchema = parentProps[fieldName];
        if (isObj(propSchema)) {
          properties[fieldName] = compileNode(propSchema, fieldName, true);
        }
      }
      variants.push({
        discriminator,
        node: {
          kind: "object",
          required: variantRequired,
          properties,
        },
      });
    }
    const out: FormOneOfNode = {
      kind: "oneOf",
      required,
      variants,
    };
    if (name !== undefined) (out as { name?: string }).name = name;
    if (description !== undefined)
      (out as { description?: string }).description = description;
    return out;
  }

  const type = getType(schema);
  const description = getString(schema, "description");

  // Enum-shape string — render as a select.
  if (type === "string") {
    const enumVals = getEnum(schema);
    if (enumVals !== undefined) {
      const out: FormEnumNode = {
        kind: "enum",
        name: name ?? "",
        required,
        values: enumVals,
      };
      if (description !== undefined)
        (out as { description?: string }).description = description;
      return out;
    }
    const pattern = getString(schema, "pattern");
    const formatKw = getString(schema, "format");
    const minLength = getNumber(schema, "minLength");
    const maxLength = getNumber(schema, "maxLength");
    const format = detectFormat(pattern, formatKw);
    const lookupEntity = detectLookupEntity(name, pattern);
    const out: FormTextNode = {
      kind: "text",
      name: name ?? "",
      required,
    };
    if (description !== undefined)
      (out as { description?: string }).description = description;
    if (pattern !== undefined) (out as { pattern?: string }).pattern = pattern;
    if (minLength !== undefined)
      (out as { minLength?: number }).minLength = minLength;
    if (maxLength !== undefined)
      (out as { maxLength?: number }).maxLength = maxLength;
    if (format !== undefined) (out as { format?: FormTextFormat }).format = format;
    if (lookupEntity !== undefined)
      (out as { lookupEntity?: FormLookupEntity }).lookupEntity = lookupEntity;
    return out;
  }

  if (type === "integer" || type === "number") {
    const minimum = getNumber(schema, "minimum");
    const maximum = getNumber(schema, "maximum");
    const out: FormIntegerNode = {
      kind: "integer",
      name: name ?? "",
      required,
    };
    if (description !== undefined)
      (out as { description?: string }).description = description;
    if (minimum !== undefined) (out as { minimum?: number }).minimum = minimum;
    if (maximum !== undefined) (out as { maximum?: number }).maximum = maximum;
    return out;
  }

  if (type === "boolean") {
    const out: FormBooleanNode = {
      kind: "boolean",
      name: name ?? "",
      required,
    };
    if (description !== undefined)
      (out as { description?: string }).description = description;
    return out;
  }

  if (type === "array") {
    const itemSchema = isObj(schema["items"])
      ? (schema["items"] as JsonSchema)
      : { type: "string" };
    const itemNode = compileNode(itemSchema, undefined, true);
    const out: FormArrayNode = {
      kind: "array",
      name: name ?? "",
      required,
      itemNode,
    };
    if (description !== undefined)
      (out as { description?: string }).description = description;
    return out;
  }

  // Object — recurse into properties.
  if (type === "object" || type === undefined) {
    const propsRaw = isObj(schema["properties"])
      ? (schema["properties"] as Record<string, JsonSchema>)
      : {};
    const required = Array.isArray(schema["required"])
      ? (schema["required"] as unknown[]).filter(
          (s): s is string => typeof s === "string",
        )
      : [];
    const properties: Record<string, FormTreeNode> = {};
    for (const [propName, propSchema] of Object.entries(propsRaw)) {
      if (isObj(propSchema)) {
        properties[propName] = compileNode(
          propSchema,
          propName,
          required.includes(propName),
        );
      }
    }
    const out: FormObjectNode = {
      kind: "object",
      required,
      properties,
    };
    if (name !== undefined) (out as { name?: string }).name = name;
    if (description !== undefined)
      (out as { description?: string }).description = description;
    return out;
  }

  // Unknown type — fall back to plain text with no constraints.
  const out: FormTextNode = {
    kind: "text",
    name: name ?? "",
    required,
  };
  if (description !== undefined)
    (out as { description?: string }).description = description;
  return out;
}
